import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useToast } from "@/hooks/use-toast";
import logo from "@/assets/logo-mente-sa.png";

const ResetPassword = () => {
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [loading, setLoading] = useState(false);
  const [ready, setReady] = useState(false);
  const navigate = useNavigate();
  const { toast } = useToast();

  useEffect(() => {
    const init = async () => {
      // PKCE flow: ?code=... in query string
      const url = new URL(window.location.href);
      const code = url.searchParams.get("code");
      if (code) {
        const { error } = await supabase.auth.exchangeCodeForSession(code);
        if (!error) {
          setReady(true);
          // clean the URL
          window.history.replaceState({}, "", "/reset-password");
          return;
        } else {
          toast({ title: "Link inválido", description: error.message, variant: "destructive" });
        }
      }
      // Hash-based recovery (#access_token=...&type=recovery), supabase-js auto-processes it
      const { data } = await supabase.auth.getSession();
      if (data.session) setReady(true);
    };
    init();

    const { data: sub } = supabase.auth.onAuthStateChange((event) => {
      if (event === "PASSWORD_RECOVERY" || event === "SIGNED_IN") setReady(true);
    });
    return () => sub.subscription.unsubscribe();
  }, [toast]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (password !== confirm) {
      toast({ title: "Erro", description: "As palavras-passe não coincidem.", variant: "destructive" });
      return;
    }
    setLoading(true);
    const { error } = await supabase.auth.updateUser({ password });
    setLoading(false);
    if (error) {
      toast({ title: "Erro", description: error.message, variant: "destructive" });
    } else {
      toast({ title: "Palavra-passe atualizada", description: "A redirecionar..." });
      setTimeout(() => navigate("/auth"), 1200);
    }
  };

  return (
    <div className="min-h-screen bg-background flex items-center justify-center px-4">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <a href="/" className="inline-flex items-center gap-3 mb-4">
            <img src={logo} alt="Mente Sã" className="h-16 w-16 rounded-full object-cover shadow-md" />
            <span className="font-display text-3xl font-bold text-primary">Mente Sã</span>
          </a>
          <p className="text-muted-foreground">Defina uma nova palavra-passe</p>
        </div>

        <form onSubmit={handleSubmit} className="rounded-2xl bg-card border border-border p-8 space-y-5">
          {!ready && (
            <p className="text-sm text-muted-foreground text-center">
              Abra o link de recuperação enviado para o seu email para continuar.
            </p>
          )}
          <div className="space-y-2">
            <Label htmlFor="password">Nova palavra-passe</Label>
            <Input id="password" type="password" value={password} onChange={(e) => setPassword(e.target.value)} required minLength={6} />
          </div>
          <div className="space-y-2">
            <Label htmlFor="confirm">Confirmar palavra-passe</Label>
            <Input id="confirm" type="password" value={confirm} onChange={(e) => setConfirm(e.target.value)} required minLength={6} />
          </div>
          <Button type="submit" className="w-full rounded-full" disabled={loading || !ready}>
            {loading ? "A atualizar..." : "Atualizar palavra-passe"}
          </Button>
        </form>
      </div>
    </div>
  );
};

export default ResetPassword;
